import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useAtom } from 'jotai'
import { ChangeEvent, useContext, useMemo, useState, useEffect, useRef } from 'react'
import { TransactionContext } from '../../../../../app/contexts/TransactionContext'
import { Transaction } from '../../../../../app/entities/Transaction'
import { selectedIdfromTableRowTransaction } from '../../../../../app/store'
import { TableColumsDefinition } from './components/TableColumnsDefinition'

export const useTransactionTableController = () => {
  const { transaction, setSearchValue } = useContext(TransactionContext)
  const { data, isFetching, isLoading, fetchNextPage, isFetchingNextPage } = useMemo(
    () => transaction || {},
    [transaction],
  )

  const shouldShowInfiniteScroll = useMemo(() => {
    if (!data?.transactions || !data?.pagination?.limit) return false
    return data.transactions.length >= data.pagination.limit
  }, [data?.transactions])

  const [selectedItem, setSelectedItem] = useState<Transaction | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const { columns } = TableColumsDefinition()
  const [, setSelectedId] = useAtom(selectedIdfromTableRowTransaction)
  const observerRef = useRef<HTMLDivElement>(null)

  const filteredData = data?.transactions

  const table = useReactTable({
    data: filteredData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  useEffect(() => {
    if (!observerRef.current || !shouldShowInfiniteScroll || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && typeof fetchNextPage === 'function' && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px',
      }
    )

    observer.observe(observerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [fetchNextPage, shouldShowInfiniteScroll, isFetchingNextPage, data?.transactions?.length])

  const handleRowClick = (item: Transaction) => {
    setSelectedItem(item)
    setSelectedId(item._id)
  }

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    setSearchValue(inputValue.replace(/[^a-zA-Z0-9\s]/g, ''))
  }

  const handleSheetState = () => {
    setSheetOpen(!sheetOpen)
  }

  return {
    selectedItem,
    table,
    handleRowClick,
    handleSearchInputChange,
    handleSheetState,
    sheetOpen,
    setSheetOpen,
    isFetching,
    isLoading,
    isFetchingNextPage,
    observerRef,
    shouldShowInfiniteScroll,
  }
}
